'use client';

import 'instantsearch.css/themes/reset.css';

import { InstantMeiliSearchInstance } from '@meilisearch/instant-meilisearch';
import { Configure, InstantSearch, SearchBox, useInstantSearch } from 'react-instantsearch';
import CustomPagination from '@/components/search/custom/CustomPagination';
import initializeClient from '@/lib/meilisearch/client';
import { useEffect, useState, useRef } from 'react';
import { INDEX_ASSETS, INDEX_GLOBAL, INDEX_PAGES } from './types';

import TitleH2 from '../common/TitleH2';
import CustomHits from './hits/CustomHit';
import { mapGlobalHit } from '@/lib/mappers/search/mapGlobalHit';
import CustomMenu from './custom/CustomMenu';
import CustomSortBy from './custom/CustomSortBy';
import { AppConfig } from '@/utils/AppConfig';
interface Props {
  title?: string;
  displaySearchBar?: boolean;
}

export default function Search({ title, displaySearchBar = true }: Props) {
  let [searchClient, setSearchClient] = useState<InstantMeiliSearchInstance | null>(null);
  let [currentPage, setCurrentPage] = useState<number | null>(null);
  const searchElement = useRef<null | HTMLDivElement>(null);

  const executeScroll = () =>
    searchElement?.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });

  useEffect(() => {
    const initClient = async () => {
      const searchClient = await initializeClient();
      setSearchClient(searchClient);
    };

    initClient();
  }, []);

  useEffect(() => {
    if (currentPage !== null) {
      executeScroll();
    }
  }, [currentPage]);

  function PaginationScroll() {
    const { indexUiState } = useInstantSearch();
    useEffect(() => {
      setCurrentPage(indexUiState.page || 0);
    }, [indexUiState]);

    return <></>;
  }

  return (
    <div className="flex min-h-[800px] flex-col lg:px-11 lg:py-2.5" ref={searchElement}>
      <div className="gap-5">
        {title && (
          <TitleH2
            size="medium"
            customFont={true}
            color="black"
            titleTracking="tracking-wide"
            heading={true}
          >
            {title}
          </TitleH2>
        )}
      </div>
      {!searchClient && (
        <div className="flex w-full flex-col gap-4">
          <div className="flex items-center gap-4">
            <div className="skeleton h-16 w-16 shrink-0 rounded-full"></div>
            <div className="flex flex-col gap-4">
              <div className="skeleton h-4 w-20"></div>
              <div className="skeleton h-4 w-28"></div>
            </div>
          </div>
          <div className="skeleton h-full w-full"></div>
        </div>
      )}
      {searchClient && (
        <InstantSearch
          indexName={`${INDEX_GLOBAL}:date_published:desc`}
          searchClient={searchClient}
        >
          <div className="relative mx-auto flex w-full animate-appear flex-col items-center justify-center opacity-0 transition-opacity duration-1000 ease-in-out">
            {displaySearchBar && (
              <SearchBox
                classNames={{
                  root: 'w-full p-1',
                  form: 'relative w-full',
                  input:
                    'block h-12 w-full pl-14 pr-5 py-2 bg-white border border-slate-300 placeholder-slate-400 focus:outline-none focus:border-sky-500 focus:ring-sky-500 rounded-full focus:ring-1',
                  submitIcon: 'absolute top-4 left-6 bottom-0 w-5 h-5',
                  resetIcon: 'absolute top-4 right-6 bottom-0 w-5 h-5',
                }}
              ></SearchBox>
            )}
            <div className="flex w-full flex-wrap gap-2.5 pb-8">
              <CustomSortBy
                items={[
                  { value: `${INDEX_GLOBAL}`, label: 'Trier par date' },
                  { value: `${INDEX_GLOBAL}:date_published:desc`, label: 'Plus récent' },
                  { value: `${INDEX_GLOBAL}:date_published:asc`, label: 'Plus ancien' },
                ]}
              />
              <CustomMenu attribute="type" limit={AppConfig.meilisearchCustomMenuLimit} />
              <CustomMenu attribute="categories" limit={AppConfig.meilisearchCustomMenuLimit} />
            </div>
            <CustomHits hitMapper={mapGlobalHit} searchType="news" />
          </div>
          {/* @ts-ignore */}
          <Configure hitsPerPage={12} filters={`type!=${INDEX_PAGES} AND type!=${INDEX_ASSETS}`} />
          <CustomPagination
            padding={2}
            classNames={{
              root: 'flex w-full my-8 justify-center',
              list: 'MyCustomPaginationList MyCustomPaginationList--subclass',
              item: 'flex mx-5 justify-center items-center gap-4 h-10 font-bold',
              selectedItem:
                'flex mx-2 justify-center items-center w-14 h-14 rounded-full gradient-circle font-bold text-white',
            }}
          />
          <PaginationScroll />
        </InstantSearch>
      )}
    </div>
  );
}
